import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetTransactionsSectionPermissions } from 'modules/transactions/hooks/useGetTransactionsSectionPermissions';
import { useGetOperations } from 'modules/transactions/hooks/useGetOperations';
import { setModal } from 'modules/_shared/redux/modalActions';
import { UpdateOperationModlaProvider } from 'modules/_shared/components/Modals/UpdateOperationModal/context/UpdateOperationModalContext';
import UpdateOperation from 'modules/_shared/components/Modals/UpdateOperationModal';

import { operationsListPageInitValue } from '../constants/operationsListPageInitValue';
import { OperationsListPageContextType } from '../types/OperationsListPageContextType';
import { operationsWithoutNotarialRegistrationSection } from '../../OperationDetails/constants/operationsWithoutNotarialRegistrationSection';
import { useGetOperationsCount } from '../hooks/useGetOperationsCount';

const OperationsListPageContext = createContext<OperationsListPageContextType>(
  operationsListPageInitValue
);

export const OperationsListPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();

  const { societyId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const operations = useSelector((state: State) => state.operations || []);

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [filters, setFilters] = useState<string[]>([]);
  const [refetchKey, setRefetchKey] = useState(0);

  useGetActualSociety(societyId);
  useGetMenu();

  const { data: permissionsData } = useGetTransactionsSectionPermissions();

  const { isLoading } = useGetOperations({
    societyId,
    page,
    rowsPerPage,
    filters,
    refetchKey,
  });

  const { data: operationsCountData } = useGetOperationsCount({
    society: actualSociety,
    filters,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const invalidateOperations = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleChangeFilter = (filters: string[]) => {
    setPage(1);
    setFilters(filters);
    invalidateOperations();
  };

  const handleEdit = (index: number) => {
    const operation = operations[index];
    if (!operation) return;

    const hasNotarialRegistration =
      !operationsWithoutNotarialRegistrationSection.includes(
        operation.operationType
      );

    dispatch(
      setModal(
        <UpdateOperationModlaProvider
          operation={operation}
          hasNotarialRegistration={hasNotarialRegistration}
        >
          <UpdateOperation />
        </UpdateOperationModlaProvider>
      )
    );
  };

  return (
    <OperationsListPageContext.Provider
      value={{
        data: {
          society: actualSociety,
          societyNominalValue: actualSociety?.nominalValue ?? 0,
          operationsTotal: operationsCountData.operationsCount || 0,
          operations,
          page,
          rowsPerPage,
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleChangeFilter,
          handleEdit,
        },
      }}
    >
      {children}
    </OperationsListPageContext.Provider>
  );
};

export const useOperationsListPage = () => {
  const context = useContext(OperationsListPageContext);
  if (!context) {
    throw new Error(
      'useOperationsListPage must be used within a OperationsListPageProvider'
    );
  }
  return context;
};
